import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';
import { AuthProvider } from './contexts/AuthContext';
import { LanguageProvider } from './contexts/LanguageContext';
import { ToastProvider } from './components/common/Toast';
import ErrorBoundary from './components/ErrorBoundary';
import HomePage from './pages/HomePage';
import DevTest from './components/DevTest';
import LoginSelection from './pages/LoginSelection';
import GarageLogin from './pages/GarageLogin';
import GarageRegister from './pages/GarageRegister';
import CustomerLogin from './pages/CustomerLogin';
import Dashboard from './pages/Dashboard';
import PricingPage from './pages/PricingPage';
import VehiclesPage from './pages/VehiclesPage';
import CustomersPage from './pages/CustomersPage';
import JobCardsPage from './pages/JobCardsPage';
import InventoryManagement from './pages/inventory/InventoryManagement';
import InvoicesPage from './pages/InvoicesPage';
import PaymentsPage from './pages/PaymentsPage';
import EmployeesPage from './pages/EmployeesPage';
import SuperAdminDashboard from './components/dashboard/SuperAdminDashboard';
import AdminDashboard from './components/dashboard/EnhancedAdminDashboard';
import GarageOwnerDashboard from './components/dashboard/EnhancedGarageOwnerDashboard';
import EmployeeDashboard from './components/dashboard/EnhancedEmployeeDashboard';
import CustomerDashboard from './components/dashboard/EnhancedCustomerDashboard';
import PrivateRoute from './components/common/PrivateRoute';
import GarageManagement from './pages/admin/GarageManagement';
import GarageEdit from './pages/admin/GarageEdit';
import UserManagement from './pages/admin/UserManagement';
import SubscriptionManagement from './pages/admin/SubscriptionManagement';
import ReportsPage from './pages/admin/ReportsPage';
import SettingsPage from './pages/admin/SettingsPage';
import AdminPrivilegesPage from './pages/admin/AdminPrivilegesPage';
import RevenueSharingManagement from './pages/admin/RevenueSharingManagement';
import CustomerCheckIn from './pages/CustomerCheckIn';
// import FinancialReports from './pages/admin/FinancialReports';
// import GaragePerformance from './pages/admin/GaragePerformance';
import './App.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function App() {
  // Determine basename based on URL
  const basename = window.location.pathname.startsWith('/rayyi7-dev') ? '/rayyi7-dev' : 
                   window.location.pathname.startsWith('/dev') ? '/dev' : '';
  
  console.log('App.tsx - Initial load:', {
    pathname: window.location.pathname,
    basename: basename,
    href: window.location.href,
    hasToken: !!localStorage.getItem('access_token'),
    hasUser: !!localStorage.getItem('user')
  });
  
  // Add a redirect handler to catch any redirects
  useEffect(() => {
    console.log('App.tsx - useEffect location:', window.location.pathname);
    
    // Log any navigation changes
    const logNavigation = () => {
      console.log('App.tsx - Navigation change detected:', {
        pathname: window.location.pathname,
        hasToken: !!localStorage.getItem('access_token'),
        hasUser: !!localStorage.getItem('user')
      });
    };
    
    window.addEventListener('popstate', logNavigation);
    return () => window.removeEventListener('popstate', logNavigation);
  }, []);
  
  // API base URL is now handled correctly in api/config.ts
  
  // Add base tag for /dev or /rayyi7-dev environment
  useEffect(() => {
    if (basename === '/dev' || basename === '/rayyi7-dev') {
      const base = document.querySelector('base');
      if (!base) {
        const baseTag = document.createElement('base');
        baseTag.href = basename + '/';
        document.head.insertBefore(baseTag, document.head.firstChild);
      }
    }
  }, [basename]);
  
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <ToastProvider>
          <LanguageProvider>
            <AuthProvider>
              <Router basename={basename}>
              <div className="App">
              {/* Dev Mode Banner */}
              {(basename === '/rayyi7-dev' || basename === '/dev') && (
                <div className="bg-yellow-400 border-b-2 border-yellow-600 text-black px-4 py-3 text-center font-bold text-sm shadow-lg fixed top-0 left-0 right-0 z-50">
                  <div className="flex items-center justify-center space-x-2">
                    <span className="text-lg">🚧</span>
                    <span>DEVELOPMENT MODE</span>
                    <span className="text-lg">🚧</span>
                    <span className="hidden sm:inline">- This is the development version of Rayyi7</span>
                  </div>
                </div>
              )}
              
              {/* Add padding to body when dev banner is present */}
              <div className={(basename === '/rayyi7-dev' || basename === '/dev') ? 'pt-12' : ''}>
              <Routes>
              {/* Public routes */}
              <Route path="/" element={<HomePage />} />
              <Route path="/pricing" element={<PricingPage />} />
              <Route path="/login" element={<LoginSelection />} />
              <Route path="/login/garage" element={<GarageLogin />} />
              <Route path="/register/garage" element={<GarageRegister />} />
              <Route path="/login/customer" element={<CustomerLogin />} />
              
              {/* Protected routes - Role-based Dashboards */}
              <Route
                path="/dashboard"
                element={
                  <PrivateRoute>
                    <Dashboard />
                  </PrivateRoute>
                }
              />
              <Route
                path="/admin/dashboard"
                element={
                  <PrivateRoute>
                    <SuperAdminDashboard />
                  </PrivateRoute>
                }
              />
              <Route
                path="/admin-user/dashboard"
                element={
                  <PrivateRoute>
                    <AdminDashboard />
                  </PrivateRoute>
                }
              />
              <Route
                path="/garage-owner/dashboard"
                element={
                  <PrivateRoute>
                    <GarageOwnerDashboard />
                  </PrivateRoute>
                }
              />
              <Route
                path="/employee/dashboard"
                element={
                  <PrivateRoute>
                    <EmployeeDashboard />
                  </PrivateRoute>
                }
              />
              {/* Dashboard sub-routes */}
              <Route
                path="/dashboard/vehicles"
                element={
                  <PrivateRoute>
                    <VehiclesPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/dashboard/customers"
                element={
                  <PrivateRoute>
                    <CustomersPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/dashboard/customer-checkin"
                element={
                  <PrivateRoute>
                    <CustomerCheckIn />
                  </PrivateRoute>
                }
              />
              <Route
                path="/dashboard/job-cards"
                element={
                  <PrivateRoute>
                    <JobCardsPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/job-cards/new"
                element={
                  <PrivateRoute>
                    <JobCardsPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/dashboard/inventory"
                element={
                  <PrivateRoute>
                    <InventoryManagement />
                  </PrivateRoute>
                }
              />
              <Route
                path="/dashboard/invoices"
                element={
                  <PrivateRoute>
                    <InvoicesPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/dashboard/payments"
                element={
                  <PrivateRoute>
                    <PaymentsPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/dashboard/reports"
                element={
                  <PrivateRoute>
                    <ReportsPage />
                  </PrivateRoute>
                }
              />
              {/* <Route
                path="/dashboard/financial-reports"
                element={
                  <PrivateRoute>
                    <FinancialReports />
                  </PrivateRoute>
                }
              />
              <Route
                path="/dashboard/garage-performance"
                element={
                  <PrivateRoute>
                    <GaragePerformance />
                  </PrivateRoute>
                }
              /> */}
              <Route
                path="/dashboard/garages"
                element={
                  <PrivateRoute>
                    <GarageManagement />
                  </PrivateRoute>
                }
              />
              <Route
                path="/dashboard/garages/:id/edit"
                element={
                  <PrivateRoute>
                    <GarageEdit />
                  </PrivateRoute>
                }
              />
              <Route
                path="/dashboard/users"
                element={
                  <PrivateRoute>
                    <UserManagement />
                  </PrivateRoute>
                }
              />
              <Route
                path="/dashboard/subscriptions"
                element={
                  <PrivateRoute>
                    <SubscriptionManagement />
                  </PrivateRoute>
                }
              />
              <Route
                path="/dashboard/revenue-sharing"
                element={
                  <PrivateRoute>
                    <RevenueSharingManagement />
                  </PrivateRoute>
                }
              />
              <Route
                path="/dashboard/employees"
                element={
                  <PrivateRoute>
                    <EmployeesPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/dashboard/tasks"
                element={
                  <PrivateRoute>
                    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                      <div className="text-center">
                        <h1 className="text-2xl font-bold text-gray-900 mb-4">My Tasks</h1>
                        <p className="text-gray-600">Coming Soon - Employee task management page</p>
                      </div>
                    </div>
                  </PrivateRoute>
                }
              />
              <Route
                path="/dashboard/service-history"
                element={
                  <PrivateRoute>
                    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                      <div className="text-center">
                        <h1 className="text-2xl font-bold text-gray-900 mb-4">Service History</h1>
                        <p className="text-gray-600">Coming Soon - Customer service history page</p>
                      </div>
                    </div>
                  </PrivateRoute>
                }
              />
              <Route
                path="/dashboard/appointments"
                element={
                  <PrivateRoute>
                    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                      <div className="text-center">
                        <h1 className="text-2xl font-bold text-gray-900 mb-4">Appointments</h1>
                        <p className="text-gray-600">Coming Soon - Appointment management page</p>
                      </div>
                    </div>
                  </PrivateRoute>
                }
              />
              <Route
                path="/dashboard/settings"
                element={
                  <PrivateRoute>
                    <SettingsPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/dashboard/admin-privileges"
                element={
                  <PrivateRoute>
                    <AdminPrivilegesPage />
                  </PrivateRoute>
                }
              />
              
              {/* Keep old routes for backward compatibility */}
              <Route
                path="/vehicles"
                element={
                  <PrivateRoute>
                    <VehiclesPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/customers"
                element={
                  <PrivateRoute>
                    <CustomersPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/job-cards"
                element={
                  <PrivateRoute>
                    <JobCardsPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/inventory"
                element={
                  <PrivateRoute>
                    <InventoryManagement />
                  </PrivateRoute>
                }
              />
              
              {/* Protected routes - Customers */}
              <Route
                path="/customer/dashboard"
                element={
                  <PrivateRoute>
                    <CustomerDashboard />
                  </PrivateRoute>
                }
              />
              
              {/* Catch all redirect */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
            
            {/* Toast notifications */}
            <Toaster
              position="top-right"
              toastOptions={{
                duration: 4000,
                style: {
                  background: '#363636',
                  color: '#fff',
                },
                success: {
                  style: {
                    background: '#10B981',
                  },
                },
                error: {
                  style: {
                    background: '#EF4444',
                  },
                },
              }}
            />
            </div>
            
          </div>
        </Router>
      </AuthProvider>
      </LanguageProvider>
    </ToastProvider>
    </QueryClientProvider>
  </ErrorBoundary>
  );
}

export default App;